<script lang="ts">
  import { navigationIntent, type NavigationIntent } from '../actions/navigation-intent';
  import type { CrossReference, CrossReferenceNavigation, Testament, Verse } from '../reader/types';

  interface DetailTab {
    key: 'greek' | 'hebrew' | 'lxx' | 'cross-references';
    label: string;
  }

  interface CompletionControl {
    label: string;
    checked: boolean;
    onToggle: () => void;
  }

  let {
    bookName,
    chapter,
    verses,
    testament,
    highlightedVerse = null,
    highlightedVerseNonce = 0,
    completionControl = null,
    onReference,
  }: {
    bookName: string;
    chapter: number;
    verses: Verse[];
    testament: Testament;
    highlightedVerse?: number | null;
    highlightedVerseNonce?: number;
    completionControl?: CompletionControl | null;
    onReference?: (reference: CrossReferenceNavigation, intent: NavigationIntent) => void;
  } = $props();

  let openVerse = $state<number | null>(null);
  let activeHighlightVerse = $state<number | null>(null);
  let detailTabIndices = $state<Record<number, number>>({});

  $effect(() => {
    highlightedVerseNonce;

    if (highlightedVerse === null) {
      activeHighlightVerse = null;
      return;
    }

    activeHighlightVerse = highlightedVerse;
    const timeoutId = setTimeout(() => {
      if (activeHighlightVerse === highlightedVerse) {
        activeHighlightVerse = null;
      }
    }, 5000);

    return () => clearTimeout(timeoutId);
  });

  function toggleVerse(verseNumber: number): void {
    openVerse = openVerse === verseNumber ? null : verseNumber;
  }

  function tabsFor(verse: Verse): DetailTab[] {
    const tabs: DetailTab[] =
      testament === 'OT'
        ? [
            { key: 'hebrew', label: 'Hebrew' },
            { key: 'lxx', label: 'Septuagint' },
          ]
        : [{ key: 'greek', label: 'Greek' }];

    if ((verse.crossReferences?.length ?? 0) > 0) {
      tabs.push({ key: 'cross-references', label: 'Cross References' });
    }

    return tabs;
  }

  function activeTabFor(verse: Verse): DetailTab {
    const tabs = tabsFor(verse);
    const storedIndex = detailTabIndices[verse.verse] ?? 0;
    return tabs[Math.min(storedIndex, tabs.length - 1)] ?? tabs[0];
  }

  function selectTab(verse: Verse, index: number): void {
    detailTabIndices = { ...detailTabIndices, [verse.verse]: index };
  }

  function jumpToReference(reference: CrossReference, intent: NavigationIntent): void {
    onReference?.({
      bookId: reference.bookId,
      chapter: reference.chapter,
      verse: reference.verse,
    }, intent);
  }
</script>

<article class="chapter">
  <header class="chapter__header" data-cy="chapter-card-heading">
    <h2 class="chapter__book" data-cy="chapter-card-book">{bookName}</h2>
    <span class="chapter__chapter" data-cy="chapter-card-chapter" aria-label={`Chapter ${chapter}`}>
      {chapter}
    </span>
  </header>
  <div class="chapter__body">
    <div class="chapter__verses">
      {#each verses as verse (verse.verse)}
        {@const tabs = tabsFor(verse)}
        {@const activeTab = activeTabFor(verse)}
        <section class="verse-item">
          <button
            type="button"
            class:verse-trigger--expanded={openVerse === verse.verse}
            class:verse-trigger--search-hit={activeHighlightVerse === verse.verse}
            class="verse verse-trigger"
            data-cy={`verse-trigger-${verse.verse}`}
            data-verse={verse.verse}
            aria-expanded={openVerse === verse.verse}
            onclick={() => toggleVerse(verse.verse)}
          >
            <sup class="verse-num">{verse.verse}</sup>{verse.text}
          </button>

          {#if openVerse === verse.verse}
            <div class="verse-detail" data-cy={`verse-detail-${verse.verse}`}>
              {#if tabs.length > 1}
                <div class="verse-detail__tabs" role="tablist" aria-label={`Verse ${verse.verse} details`}>
                  {#each tabs as tab, index (tab.key)}
                    <button
                      type="button"
                      class="mdc-tab verse-detail__tab"
                      class:is-selected={activeTab.key === tab.key}
                      role="tab"
                      aria-selected={activeTab.key === tab.key}
                      onclick={() => selectTab(verse, index)}
                    >
                      {tab.label}
                    </button>
                  {/each}
                </div>
              {/if}

              {#if activeTab.key === 'hebrew'}
                <div class="verse-source" data-cy="verse-source-hebrew">
                  <p class="verse-source__text verse-source__text--hebrew" lang="he" dir="rtl">
                    {verse.hebrewText || 'No aligned Hebrew text available for this verse.'}
                  </p>
                </div>
              {:else if activeTab.key === 'lxx'}
                <div class="verse-source" data-cy="verse-source-lxx">
                  <p class="verse-source__text" lang="el">
                    {verse.lxxText || 'No aligned Septuagint text available for this verse.'}
                  </p>
                </div>
              {:else if activeTab.key === 'cross-references'}
                <div
                  class="verse-source verse-source--cross-references"
                  data-cy="verse-source-cross-references"
                >
                  <div class="verse-cross-references">
                    {#each verse.crossReferences ?? [] as reference (reference.id)}
                      <button
                        type="button"
                        class="verse-cross-references__item"
                        data-cy="cross-reference-item"
                        data-reference={reference.label}
                        use:navigationIntent={{
                          onIntent: ({ intent }) => jumpToReference(reference, intent),
                        }}
                      >
                        <span class="verse-cross-references__label">{reference.label}</span>
                        <span class="verse-cross-references__text">{reference.text}</span>
                      </button>
                    {/each}
                  </div>
                </div>
              {:else}
                <div class="verse-source" data-cy="verse-source-greek">
                  <p class="verse-source__text" lang="el">
                    {verse.greekText || 'No aligned Greek text available for this verse.'}
                  </p>
                </div>
              {/if}
            </div>
          {/if}
        </section>
      {/each}
    </div>
  </div>
  {#if completionControl}
    <footer class="chapter-control-surface" data-cy="chapter-control-surface">
      <button
        type="button"
        class:chapter-control-toggle--checked={completionControl.checked}
        class="chapter-control-toggle"
        data-cy="chapter-mark-read"
        aria-pressed={completionControl.checked}
        onclick={completionControl.onToggle}
      >
        <span class="chapter-control-toggle__indicator" aria-hidden="true"></span>
        {completionControl.label}
      </button>
    </footer>
  {/if}
</article>
